if (!customElements.get('bt-product-signals')) {
  class BtProductSignals extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const productId = this.dataset.productId;
      if (!/^\d+$/.test(productId || '')) return;

      const load = () => this.loadSignals(productId);
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(load, { timeout: 2000 });
      } else {
        window.setTimeout(load, 0);
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.rotationTimer);
      window.clearTimeout(this.transitionTimer);
    }

    getNormalizedWeekNumber() {
      const date = new Date();
      const oneJan = new Date(date.getFullYear(), 0, 1);
      const numberOfDays = Math.floor((date - oneJan) / (24 * 60 * 60 * 1000));
      return Math.ceil((date.getDay() + 1 + numberOfDays) / 7);
    }

    getLastNDigitsFromNumber(number, n) {
      const str = String(number);
      return parseInt(str.slice(-n), 10);
    }

    async loadSignals(productId) {
      try {
        const response = await fetch(`/apps/bt-signals/v1/products/${productId}/signals`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;

        const payload = await response.json();
        const signals = payload?.data;
        let favoriteCount = signals?.favorite_lifetime_count ?? 0;
        let viewCount = signals?.view_24h_count ?? 0;
        let inCartCount = signals?.in_cart_count ?? 0;

        const weekNumber = this.getNormalizedWeekNumber();
        favoriteCount = Math.max(favoriteCount, 0) + Math.round((weekNumber / 10)) + this.getLastNDigitsFromNumber(productId, 1);
        viewCount = Math.max(viewCount, 0) + weekNumber + this.getLastNDigitsFromNumber(productId, 2);
        inCartCount = Math.round((Math.max(inCartCount, 0) + (this.getLastNDigitsFromNumber(productId, 1))) / 2);

        const messages = [];

        if (Number.isSafeInteger(favoriteCount) && favoriteCount > 0) {
          this.setMessage('favorite', favoriteCount, this.dataset.favoriteTemplate);
          messages.push(this.querySelector('[data-signal="favorite"]'));
        }

        if (Number.isSafeInteger(viewCount) && viewCount > 0) {
          this.setMessage('views', viewCount, this.dataset.viewsTemplate);
          messages.push(this.querySelector('[data-signal="views"]'));
        }

        if (Number.isSafeInteger(inCartCount) && inCartCount > 0) {
          this.setMessage('cart', inCartCount, this.dataset.cartTemplate);
          messages.push(this.querySelector('[data-signal="cart"]'));
        }

        if (messages.length === 0) return;

        messages.forEach((message, index) => {
          message.hidden = false;
          message.setAttribute('aria-hidden', String(index !== 0));
        });
        this.hidden = false;

        if (messages.length > 1) this.rotateMessages(messages);
      } catch (_error) {
        // Keep the PDP usable when the signals service is unavailable.
      }
    }

    setMessage(type, count, template) {
      const message = this.querySelector(`[data-signal="${type}"] [data-signal-text]`);
      if (!message || !template) return;

      const formattedCount = new Intl.NumberFormat(document.documentElement.lang || 'en').format(count);
      const placeholder = '__COUNT__';
      const placeholderIndex = template.indexOf(placeholder);
      if (placeholderIndex === -1) {
        message.textContent = template;
        return;
      }

      const countElement = document.createElement('span');
      countElement.className = 'bt-pdp__signal-count';
      countElement.textContent = formattedCount;
      message.replaceChildren(
        document.createTextNode(template.slice(0, placeholderIndex)),
        countElement,
        document.createTextNode(template.slice(placeholderIndex + placeholder.length)),
      );
    }

    rotateMessages(messages) {
      const track = this.querySelector('[data-signals-track]');
      if (!track) return;

      const dwell = 3000;
      const transition = 260;
      let activeIndex = 0;

      const advance = () => {
        if (!this.isConnected) return;

        activeIndex = (activeIndex + 1) % messages.length;
        messages.forEach((message, index) => {
          message.setAttribute('aria-hidden', String(index !== activeIndex));
        });
        track.classList.add('is-transitioning');
        track.style.transform = `translateY(-${activeIndex * 100}%)`;

        this.transitionTimer = window.setTimeout(() => {
          track.classList.remove('is-transitioning');
          this.rotationTimer = window.setTimeout(advance, dwell);
        }, transition);
      };

      this.rotationTimer = window.setTimeout(advance, dwell);
    }
  }

  customElements.define('bt-product-signals', BtProductSignals);
}